import { get } from "@/services/axiosUitils";

const basePath = "/formily/dynamic-model";
const EMPLOYEE_SCHEMA_NAME = "PTS Emp";

class PtsEmployeeService {
  /**
   * Search employees by firstname and/or lastname
   * Calls the base endpoint without grid data: /formily/dynamic-model/PTS%20Emp/?firstname__ilike=%M%
   * @param params - Search parameters
   * @param params.firstname - First name search term (will be wrapped with % for ilike)
   * @param params.lastname - Last name search term (will be wrapped with % for ilike)
   * @param params.page - Page number (optional, not included if not provided)
   * @param params.pageSize - Page size (optional, not included if not provided)
   * @returns Employee data
   */
  searchEmployees = async ({
    firstname,
    lastname,
    page,
    pageSize,
  }: {
    firstname?: string;
    lastname?: string;
    page?: number;
    pageSize?: number;
  }): Promise<any> => {
    const params: any = {};

    // Add pagination only if provided
    if (page !== undefined) {
      params.page = page;
    }
    if (pageSize !== undefined) {
      params.page_size = pageSize;
    }

    // Add firstname filter if provided
    if (firstname?.trim()) {
      params.firstname__ilike = `%${firstname.trim()}%`;
    }

    // Add lastname filter if provided
    if (lastname?.trim()) {
      params.lastname__ilike = `%${lastname.trim()}%`;
    }

    return await get(
      `${basePath}/${encodeURIComponent(EMPLOYEE_SCHEMA_NAME)}/`,
      {
        params,
      }
    );
  };

  /**
   * Get employees with custom filters
   * Calls the base endpoint without grid data: /formily/dynamic-model/PTS%20Emp/?filter=value
   * @param filters - Custom filter object (e.g., { firstname__ilike: '%Milan%' })
   * @param page - Page number (optional, not included if not provided)
   * @param pageSize - Page size (optional, not included if not provided)
   * @returns Employee data
   */
  getEmployees = async (
    filters?: Record<string, any>,
    page?: number,
    pageSize?: number
  ): Promise<any> => {
    const params: any = { ...filters };

    // Add pagination only if provided
    if (page !== undefined) {
      params.page = page;
    }
    if (pageSize !== undefined) {
      params.page_size = pageSize;
    }

    // Custom paramsSerializer to preserve % signs in filter values
    // This ensures %Milan% appears as %Milan% in URL, not %25Milan%25
    const paramsSerializer = (params: any) => {
      const parts: string[] = [];
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (value !== undefined && value !== null) {
          // Encode the key
          const encodedKey = encodeURIComponent(key);
          // For filter values that contain %, preserve % signs but encode other special chars
          if (typeof value === "string" && value.includes("%")) {
            // Encode everything except % signs
            const encodedValue = value
              .split("%")
              .map(part => encodeURIComponent(part))
              .join("%");
            parts.push(`${encodedKey}=${encodedValue}`);
          } else {
            // Normal encoding for other values
            parts.push(`${encodedKey}=${encodeURIComponent(String(value))}`);
          }
        }
      });
      return parts.join("&");
    };

    return await get(
      `${basePath}/${encodeURIComponent(EMPLOYEE_SCHEMA_NAME)}/`,
      {
        params,
        paramsSerializer,
      }
    );
  };

  /**
   * Get employee dashboard data
   * Calls: /pts_app/employee-dashboard/?employee_id={employeeId}
   * @param employeeId - Employee ID
   * @returns Dashboard data including today_hours, progress, check_in_out, and break_sessions
   */
  getEmployeeDashboard = async (employeeId: string): Promise<any> => {
    if (!employeeId) {
      throw new Error("Employee ID is required");
    }
    return await get("/pts_app/employee-dashboard/", {
      params: {
        employee_id: employeeId,
      },
    });
  };
}

const instance = new PtsEmployeeService();
export default instance;
